/**
 * Справка: S-39 «Чего система не видит», S-32 «Помощь», S-30 «Библиотека промтов», S-31 «Промт».
 * Тексты статические (ТЗ 7.4: моделью не генерируются), правила набора — 04-voice.
 */
import { useEffect, useState } from 'react'
import type { ScreenId } from '../router/screens.js'
import { fetchBlind, fetchPrompts, type LibraryPrompt } from '../api/sections.js'
import { copyText, hapticSuccess, getWebApp, backToChat, cloudSet } from '../telegram/call.js'
import { OB_DONE } from './data.js'
import { Icon, StatusTag } from '../components/icons/Icon.js'
import { Failed, Loading, NoShop, useAnnounce, useData, useScreenHead } from './common.js'
import { agentIcon } from './words.js'
import { Fold, LinkRow, Kv } from './ui.js'
import { fetchAgents } from '../api/client.js'

type Open = (id: ScreenId, param?: string) => void

// ── S-39 Чего система не видит ──────────────────────────────────────────────

/** Секция 1 — нет принципиально (ТЗ, S-39). Факт и причина, без «пока» и без оправданий. */
const NEVER: ReadonlyArray<{ what: string; why: string; instead: string }> = [
  { what: 'Продажи, выручка, остатки и цены чужих товаров и брендов',
    why: 'Официальный API продавца отдаёт только ваш кабинет. Данные других продавцов он не отдаёт никому.',
    instead: 'Полная картина по своему кабинету: деньги, реклама, остатки, покупатели.' },
  { what: 'Поиск ниш, спрос в категории, тренды',
    why: 'Это те же чужие продажи — их в API нет.',
    instead: 'Платные сервисы аналитики показывают чужие продажи и ниши, потому что годами копят собственную базу парсинга выдачи. Догнать её данными невозможно, и мы этого не обещаем.' },
  { what: 'Цены конкурентов и РРЦ',
    why: 'По той же причине: чужие карточки в API продавца не попадают.',
    instead: 'Своя цена, скидка и акции — в карточке товара.' },
  { what: 'СПП — скидка постоянного покупателя',
    why: 'Размер СПП API продавцу не отдаёт, и Штаб его не использует.',
    instead: 'Деньги считаются по отчёту о реализации: суммы в нём уже учитывают скидку площадки.' },
  { what: 'Позиции карточек в поиске по запросам',
    why: 'Эти методы доступны только по подписке Wildberries «Джем», а у Штаба её нет.',
    instead: 'Статистика по поисковым фразам в рекламных кампаниях.' },
  { what: 'Портрет покупателя',
    why: 'Отчёт живёт внутри подписки Wildberries и через API не выгружается.',
    instead: 'Причины недовольства из текстов отзывов, которые разбирает агент «Отзывы и вопросы».' },
  { what: 'Рейтинг продавца и надбавка к комиссии',
    why: 'Эти методы отдаются только сервисному токену, а Штаб работает на персональном.',
    instead: 'Рейтинг товара — это другая величина, и в Штабе он подписан именно так.' },
]

export function ScreenBlind({ open }: { open: Open }): React.JSX.Element {
  useScreenHead(undefined, null)
  const { s, reload } = useData(fetchBlind)
  if (s.kind === 'loading') return <Loading label="Загрузка" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (s.data.shop === null) return <NoShop reason={s.data.reason} />
  const wh = s.data.warehouses
  return (
    <div data-testid="screen-body">
      <p className="lead-note">
        Официальный API Wildberries отдаёт только ваш кабинет. Всё, что ниже, — не недоделка системы, а граница данных.
      </p>

      <h2 className="sect wb-overline"><span>Нет принципиально</span></h2>
      <section className="card">
        {NEVER.map((x, k) => (
          <Fold key={k} q={x.what} testid={'never-' + k}>
            <p><b>Почему нет.</b> {x.why}</p>
            <p><b>Что есть вместо.</b> {x.instead}</p>
          </Fold>
        ))}
      </section>

      <h2 className="sect wb-overline"><span>Нет сейчас, у всех одинаково</span></h2>
      <section className="card">
        <Fold q="Остатки по отдельным складам Wildberries" testid="now-warehouses">
          <div className="fold-tags">
            {wh?.available === true ? <StatusTag tone="good" word="Вернулось" /> : <StatusTag tone="attn" word="Скрыто Wildberries" />}
            {wh?.checked_display != null
              ? <span className="hint-inline">{wh.checked_display}</span>
              : <span className="hint-inline">статус метода не проверен</span>}
          </div>
          <p>Wildberries скрыл разрез по своим складам с 18 августа 2026 года — у всех одинаково. Ваши собственные склады видны полностью.</p>
          <p>Когда разрез вернётся, он появится в «Остатках» сам, без обновления приложения.</p>
          <button type="button" className="btn btn-soft" data-testid="blind-stock" onClick={() => open('S-16')}>Открыть остатки</button>
        </Fold>
        <Fold q="Коэффициенты приёмки, слоты и тарифы на поставку" testid="now-acceptance">
          <div className="fold-tags">
            <StatusTag tone="attn" word="Отключено Wildberries" />
            <span className="hint-inline">статус метода не проверяется</span>
          </div>
          <p>Wildberries временно отключил методы коэффициентов приёмки, тарифов на поставку и списка складов с 15–18 августа 2026 года.</p>
          <p>Как только методы вернутся, блок в «Плане поставок» заработает без обновления приложения.</p>
          <button type="button" className="btn btn-soft" data-testid="blind-plan" onClick={() => open('S-17')}>Открыть план поставок</button>
        </Fold>
      </section>
    </div>
  )
}

// ── S-32 Помощь ──────────────────────────────────────────────────────────────

const FAQ: ReadonlyArray<{ q: string; a: string[] }> = [
  { q: 'Почему выручка не сходится с кабинетом Wildberries?', a: [
    'Заказы и продажи Wildberries отдаёт как предварительные данные и обновляет раз в 30 минут. Единственная точка истины по деньгам — отчёт о реализации; он появляется позже и может отличаться.',
    'На «Сводке» выплата WB взята из закрытого недельного отчёта о реализации, а заказы — из воронки Wildberries по дате заказа.'] },
  { q: 'Что такое предварительные данные и когда они станут окончательными?', a: [
    'Пока неделя не закрыта отчётом о реализации, цифры по заказам и продажам предварительные. Отчёт за неделю Wildberries выпускает в начале следующей недели — с ним деньги за неделю становятся окончательными.'] },
  { q: 'Почему вчерашние цифры изменились?', a: [
    'Выкупы, отмены и возвраты Wildberries относит ко дню заказа. Поэтому вчерашние цифры продолжают меняться ещё несколько недель, а Штаб загружает их заново.',
    'С 14 февраля 2026 Wildberries считает в отменах и возвраты тоже — вычесть их отдельно нельзя, они уже внутри.'] },
  { q: 'Почему маржа не считается?', a: [
    'Маржа — это выплата Wildberries минус себестоимость, реклама и налог. Себестоимость Wildberries не знает, её вводит владелец в разделе «Себестоимость». Пока её нет, маржа показывается прочерком, а не нулём.'] },
  { q: 'Почему выкуп занижен на свежем периоде?', a: [
    'Wildberries не учитывает товары, которые ещё едут к покупателю. Поэтому на свежем периоде процент выкупа занижен — смотрите окно от трёх недель.'] },
  { q: 'Почему нет разреза по складам Wildberries?', a: [
    'Wildberries скрыл разрез по своим складам с 18 августа 2026 года — у всех одинаково. Ваши собственные склады видны полностью.'] },
  { q: 'Что значат пометки «можно отменить» и «отменить нельзя»?', a: [
    '«Можно отменить» — изменение возвращается, например ставка. «Можно отменить частично» — вернуть можно, но последствия могут остаться. «Отменить нельзя» — например, опубликованный ответ покупателю.',
    'Штаб читает данные кабинета и сам ничего в нём не меняет: предложенные действия вы делаете в кабинете Wildberries.'] },
  { q: 'Как отозвать ключ?', a: [
    'Удалите токен в кабинете Wildberries: Профиль → Настройки → Доступ к API. Штаб сразу перестанет получать данные; то, что уже собрано, останется.'] },
  { q: 'Что делать, если ключ истёк?', a: [
    'Создайте новый токен в кабинете Wildberries (Профиль → Настройки → Доступ к API) и пришлите его боту сообщением — бот проверит ключ и удалит сообщение из чата.',
    'Автопродления у Wildberries нет, поэтому новый ключ лучше создать заранее: срок действия виден в разделе «Ключ Wildberries».'] },
  { q: 'Как добавить сотрудника?', a: [
    'В разделе «Доступ и сотрудники» нажмите «Пригласить сотрудника» и отправьте ему ссылку. Когда он нажмёт «Старт», бот покажет код вам обоим — подтвердите, что это он.',
    'Сотрудник не видит выплату, штрафы и отчёт «Финансы».'] },
]

const BUILD: string = (import.meta.env['VITE_BUILD'] as string | undefined) || 'разработка'

export function ScreenHelp({ open }: { open: Open }): React.JSX.Element {
  useScreenHead(undefined, null)
  const tg = getWebApp()
  return (
    <div data-testid="screen-body">
      <h2 className="sect wb-overline"><span>Частые вопросы</span></h2>
      <section className="card">
        {FAQ.map((f, k) => (
          <Fold key={k} q={f.q} testid={'faq-' + k}>{f.a.map((p, i) => <p key={i}>{p}</p>)}</Fold>
        ))}
      </section>

      <h2 className="sect wb-overline"><span>Ещё о Штабе</span></h2>
      <section className="card">
        <LinkRow icon="section-help" title="Как это работает" sub="Коротко о Штабе, по шагам" testid="help-onboarding" onClick={() => open('S-01')} />
        <LinkRow icon="section-blind" title="Чего система не видит" sub="Что Wildberries не отдаёт и почему" testid="help-blind" onClick={() => open('S-39')} />
        <LinkRow icon="section-prompts" title="Библиотека промтов" sub="Что агенты получают на вход" testid="help-prompts" onClick={() => open('S-30')} />
      </section>

      <h2 className="sect wb-overline"><span>О приложении</span></h2>
      <section className="card card-pad kv" data-testid="about">
        <p><span>Сборка</span><span>{BUILD}</span></p>
        <p><span>Telegram</span><span>{tg?.version ?? '—'}</span></p>
        <p><span>Платформа</span><span>{(tg as { platform?: string } | null)?.platform ?? '—'}</span></p>
      </section>
    </div>
  )
}

// ── S-30 Библиотека промтов, S-31 Промт ─────────────────────────────────────

export function ScreenPrompts({ open }: { open: Open }): React.JSX.Element {
  useScreenHead(undefined, null)
  const { s, reload } = useData(fetchPrompts)
  if (s.kind === 'loading') return <Loading label="Загрузка промтов" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  return (
    <div data-testid="screen-body">
      <p className="lead-note">
        Это те же промты, что работают внутри системы. Внутри они получают уже посчитанные данные, поэтому цифры в отчёте всегда сверены с Wildberries.
      </p>
      <section className="card">
        {s.data.items.map((p) => (
          <button type="button" className="row row-tap has-ic prompt-row" key={p.agent} data-testid={'prompt-' + p.agent}
            onClick={() => open('S-31', p.agent)}>
            <Icon name={agentIcon(p.agent)} className="ic-muted" />
            <span className="row-main">
              <span className="row-title">{p.title}</span>
              <span className="row-meta prompt-first">{p.text}</span>
            </span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        ))}
      </section>
    </div>
  )
}

export function ScreenPrompt({ agent }: { agent: string }): React.JSX.Element {
  const { s, reload } = useData(fetchPrompts)
  const p: LibraryPrompt | undefined = s.kind === 'ok' ? s.data.items.find((x) => x.agent === agent) : undefined
  useScreenHead(p?.title, p === undefined ? null : `версия ${p.version}`)
  const announce = useAnnounce()
  const [manual, setManual] = useState(false)
  if (s.kind === 'loading') return <Loading label="Загрузка промта" rows={4} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (p === undefined) return <Failed message="Такого промта нет — возможно, ссылка устарела." />
  const copy = async (): Promise<void> => {
    const ok = await copyText(p.text)
    if (ok) { hapticSuccess(); announce({ kind: 'ok', text: 'Промт скопирован' }); return }
    // Последняя ступень деградации [F-7.4.23]: поле с выделенным текстом и подсказка.
    setManual(true)
  }
  return (
    <div data-testid="screen-body">
      <section className="card card-pad">
        {manual
          ? <>
              <textarea className="prompt-text prompt-manual" readOnly value={p.text} data-testid="prompt-manual"
                ref={(el) => { el?.focus(); el?.select() }} rows={8} />
              <p className="hint">Выделите и скопируйте вручную.</p>
            </>
          : <pre className="prompt-text" data-testid="prompt-text">{p.text}</pre>}
        <button type="button" className="btn btn-block" data-testid="prompt-copy" onClick={() => { void copy() }}>
          <Icon name="action-copy" size={20} />Скопировать промт
        </button>
      </section>
      <h2 className="sect wb-overline"><span>Как этим пользоваться</span></h2>
      <section className="card card-pad">
        <p className="body-text">Скопируйте текст, вставьте в любой чат с ИИ и замените слова в квадратных скобках своими данными.</p>
        <p className="body-text muted">Это упрощённая версия. В системе тот же агент работает на данных вашего кабинета, а каждое число и каждый артикул в ответе проверяются программой перед показом.</p>
      </section>
    </div>
  )
}

// ── S-01 Как это работает ────────────────────────────────────────────────────

/**
 * Кадры ТЗ 7.6.3 — справка, к которой возвращаются из «Помощи». Тексты сверены с тем, как Штаб
 * работает на самом деле: сервер обслуживаем мы (не «ваш»), отчёт «Отзывы» — утренний, кабинет Штаб
 * не меняет. Время отчётов — из настроек, не из вёрстки; промты — из библиотеки, не копией.
 */
function Frame({ n, rubric, title, sub, children, plate, testid }: {
  n: number; rubric: string; title: string; sub?: string; children?: React.ReactNode; plate?: string; testid?: string
}): React.JSX.Element {
  return (
    <section className="card card-pad frame" data-testid={testid ?? 'frame-' + n} aria-labelledby={'frame-' + n}>
      <p className="frame-rubric">{rubric}<span className="frame-n" aria-hidden="true">{String(n).padStart(2, '0')}</span></p>
      <h2 className="frame-title" id={'frame-' + n}>{title}</h2>
      {sub !== undefined && <p className="body-text muted">{sub}</p>}
      {children}
      {plate !== undefined && <p className="frame-plate">{plate}</p>}
    </section>
  )
}

export function ScreenOnboarding({ open }: { open: Open }): React.JSX.Element {
  useScreenHead(undefined, '9 шагов · 4 минуты')
  // Открыл справку сам (из «Помощи») — карточку первого входа на «Сводке» больше не показываем.
  useEffect(() => { void cloudSet(OB_DONE, new Date().toISOString().slice(0, 10)) }, [])
  const agents = useData(fetchAgents)
  const times = agents.s.kind === 'ok' && agents.s.data.shop !== null ? agents.s.data.items : []
  return (
    <div data-testid="screen-body" className="frames">
      <Frame n={1} rubric="Wildberries · личная система" title="Пять агентов вашего магазина"
        sub="Деньги, реклама, остатки, отзывы и общий итог. Каждый смотрит в свои цифры и приносит готовые решения — в чат с ботом и в это приложение."
        plate="Цифры считает программа по данным вашего кабинета. ИИ получает уже посчитанное и объясняет: что изменилось, почему и что сделать сегодня." />

      <Frame n={2} rubric="Шаг 1" title="Где магазин теряет деньги" sub="То, с чем Штаб помогает каждое утро:"
        plate="Каждая строка — это один агент. Дальше — кто именно за что отвечает.">
        <ul className="frame-list">
          <li>Не знаю честную маржу по каждому товару — вижу выручку, а не прибыль.</li>
          <li>Реклама ест прибыль, а ДРР смотрю раз в неделю.</li>
          <li>Товар кончается на пике продаж, а поставка приходит после.</li>
          <li>Отзывы отвечаются в последний момент и одинаковыми фразами.</li>
          <li>Цифры живут в трёх таблицах и в голове.</li>
        </ul>
      </Frame>

      <Frame n={3} rubric="Шаг 2" title="Не один чат-бот, а пять узких сотрудников"
        sub="Каждый агент работает со своими данными и отвечает за своё. Пятый сводит выводы остальных."
        plate="Агенты не спорят: «Главное» получает их выводы и расставляет порядок. Если выводы противоречат, он говорит об этом прямо.">
        <Kv rows={[
          { label: 'Финансы', text: null, bare: true, sub: 'выплата WB, штрафы, удержания, маржа по товарам' },
          { label: 'Реклама', text: null, bare: true, sub: 'расход, ДРР, ставки, фразы без заказов' },
          { label: 'Остатки и заказы', text: null, bare: true, sub: 'запас в днях, дефицит, застрявшие заказы и возвраты' },
          { label: 'Отзывы и вопросы', text: null, bare: true, sub: 'новые отзывы, жалобы, черновики ответов в стиле магазина' },
          { label: 'Главное', text: null, bare: true, sub: 'общий итог и до пяти дел на сегодня' },
        ]} />
      </Frame>

      <Frame n={4} rubric="Шаг 3" title="Агент не фантазирует — он читает ваш кабинет"
        sub="Все числа считает программа по данным Wildberries. Ни одна цифра в отчёте не придумана моделью: каждое число и каждый артикул проверяются перед отправкой."
        plate="Заказы и продажи Wildberries отдаёт как предварительные данные и обновляет раз в 30 минут. Единственная точка истины по деньгам — отчёт о реализации; он приходит позже. Выкупы, отмены и возвраты относятся ко дню заказа, поэтому вчерашние цифры меняются ещё несколько недель.">
        <Kv rows={[
          { label: 'Ключ Wildberries', text: null, bare: true, sub: 'персональный токен; хранится зашифрованным и наружу не отдаётся' },
          { label: 'То, чего Wildberries не знает', text: null, bare: true, sub: 'себестоимость, налог, постоянные расходы, срок поставки, целевой ДРР — вводите вы' },
          { label: 'Расписание', text: null, bare: true, sub: 'отчёт считается ночью и приходит в выбранное время' },
        ]} />
        <button type="button" className="link-inline" data-testid="ob-blind" onClick={() => open('S-39')}>Чего система не видит</button>
      </Frame>

      <Frame n={5} rubric="Шаг 4" title="Что приходит в Telegram" sub="Не сырая таблица, а короткий отчёт и понятные действия."
        plate="Время настраивается в разделе «Агенты». Под каждым отчётом — кнопка «Открыть отчёт» с разбором в приложении.">
        {times.length > 0
          ? <Kv rows={times.map((a) => ({ label: a.title, text: a.enabled ? a.deliver_at_local_time : 'выключен' }))} />
          : <p className="body-text">Пять утренних отчётов: финансы, реклама, остатки и заказы, отзывы и вопросы — и «Главное» последним.</p>}
      </Frame>

      <Frame n={6} rubric="Библиотека промтов" title="Что именно система говорит модели"
        sub="Эти же промты работают внутри. Их можно скопировать и прогнать в любом чат-ИИ — результат будет слабее: там нет подключённых данных."
        plate="Ограничители в промтах — не вежливость, а требование: каждый артикул и каждое число ответа обязаны совпасть с входными данными, иначе вывод вырезается.">
        <button type="button" className="btn btn-soft" data-testid="ob-prompts" onClick={() => open('S-30')}>Открыть библиотеку промтов</button>
      </Frame>

      <Frame n={7} rubric="Отзывы" title="Ответ покупателю никогда не публикуется сам"
        sub="Агент готовит черновик в стиле прошлых ответов магазина. Вы проверяете, правите и публикуете в кабинете Wildberries."
        plate="После публикации ответ проходит проверку Wildberries — обычно несколько часов. Изменить опубликованный ответ можно один раз в течение 60 дней." />

      <Frame n={8} rubric="Шаг 5" title="Что Штаб делает сам, а что — вы"
        sub="Штаб читает данные кабинета и сам ничего в нём не меняет: ставки, минус-фразы, ответы и поставки делаете вы. Предложения агентов — во вкладке «Действия»: отметьте, что сделано, а что нет."
        plate="У каждого предложения есть пометка: «можно отменить», «можно отменить частично» или «отменить нельзя» — чтобы было видно, чем рискуете.">
        <Kv rows={[
          { label: 'Можно отменить', text: null, bare: true, sub: 'ставка, пауза кампании, минус-фразы' },
          { label: 'Можно отменить частично', text: null, bare: true, sub: 'цена, участие в акции' },
          { label: 'Отменить нельзя', text: null, bare: true, sub: 'опубликованный ответ, трата рекламного бюджета' },
        ]} />
      </Frame>

      <Frame n={9} rubric="Последний шаг" title="Четыре шага до первого отчёта"
        sub="Займёт около десяти минут. Первая загрузка данных идёт в фоне — приложение можно закрыть."
        plate="Токен показывается в кабинете один раз и действует 180 дней. Автопродления у Wildberries нет — срок виден в разделе «Ключ Wildberries».">
        <ol className="frame-list">
          <li>Создать персональный токен в кабинете Wildberries: Профиль → Настройки → Доступ к API.</li>
          <li>Прислать его боту сообщением — бот проверит, что он открывает, и удалит сообщение из чата.</li>
          <li>Ввести себестоимость хотя бы у главных товаров — без неё нет маржи.</li>
          <li>Включить агентов и выбрать время отчётов.</li>
        </ol>
        <div className="pager">
          <button type="button" className="btn" data-testid="ob-key" onClick={() => open('S-06')}>Ключ Wildberries</button>
          <button type="button" className="btn btn-soft" data-testid="ob-agents" onClick={() => open('S-23')}>Агенты</button>
        </div>
      </Frame>
    </div>
  )
}

// ── S-02 … S-05 Ключ: зачем, где взять, как подключить ──────────────────────

/** Ключ принимает бот (сообщением): в приложении — инструкция и переход в чат, без полей ввода. */
export function ScreenKeyHowto({ open }: { open: Open }): React.JSX.Element {
  useScreenHead('Подключить ключ', null)
  return (
    <div data-testid="screen-body">
      <section className="card card-pad frame">
        <h2 className="frame-title">Зачем нужен ключ</h2>
        <p className="body-text">Ключ Wildberries — это доступ Штаба к данным вашего кабинета: заказы, отчёты о реализации, реклама, остатки, отзывы. Без него агентам нечего читать.</p>
        <p className="body-text muted">Ключ хранится зашифрованным и наружу не отдаётся. Отозвать его можно в любой момент — в кабинете Wildberries или в разделе «Ключ Wildberries».</p>
      </section>
      <section className="card card-pad frame gap-top">
        <h2 className="frame-title">Где взять и как подключить</h2>
        <ol className="frame-list">
          <li>Кабинет Wildberries → Профиль → Настройки → Доступ к API → «Создать токен».</li>
          <li>Тип — «Персональный». Категории: Контент, Аналитика, Цены и скидки, Маркетплейс, Статистика, Продвижение, Вопросы и отзывы, Поставки, Возвраты покупателями, Финансы.</li>
          <li>Скопируйте токен — Wildberries показывает его один раз.</li>
          <li>Пришлите его боту обычным сообщением. Бот проверит, что ключ открывает, и удалит сообщение из чата.</li>
        </ol>
        <div className="pager">
          <button type="button" className="btn" data-testid="howto-chat" onClick={() => { void backToChat() }}>Перейти в чат с ботом</button>
          <button type="button" className="btn btn-soft" data-testid="howto-key" onClick={() => open('S-06')}>Ключ Wildberries</button>
        </div>
      </section>
    </div>
  )
}
